import {useFieldArray, useForm} from 'react-hook-form';

import {mapBuyerEntriesToPayload} from '../utils';
import {buildEmptyCreateBuyerFormValues} from '../validation';
import {useAddBuyersToPurchaseMutation} from './useAddBuyersToPurchaseMutation';
import {useBuyerFormAi} from './useBuyerFormAi';

import type {CreateBuyerFormValues} from '../validation';
import type {
  AddBuyersToPurchasePayload,
  BuyerDTO,
  FatcaCrsSupplementaryPayload
} from '@sollapay/types';

interface UseAddBuyersToPurchaseFormParams {
  trustAccountId: string;
  purchaseId: string;
  supplementaryValues?: FatcaCrsSupplementaryPayload | null;
  onSuccess: (buyers: BuyerDTO[]) => void;
}

export const useAddBuyersToPurchaseForm = ({
  trustAccountId,
  purchaseId,
  supplementaryValues,
  onSuccess
}: UseAddBuyersToPurchaseFormParams) => {
  // The asset/purchasePriceNis branches of CreateBuyerFormValues exist only so this form can
  // reuse BuyerDetailsSection unchanged — they're never rendered here and never read below.
  const emptyFormValues = buildEmptyCreateBuyerFormValues([]);

  const form = useForm<CreateBuyerFormValues>({
    defaultValues: emptyFormValues,
    mode: 'onTouched'
  });

  // Single useFieldArray instance — mirrors useCreateBuyerForm's, threaded to BuyerDetailsSection via props.
  const {
    fields: buyerFields,
    append: appendBuyer,
    remove: removeBuyer,
    replace: replaceBuyers
  } = useFieldArray({control: form.control, name: 'buyers'});

  const addBuyersMutation = useAddBuyersToPurchaseMutation(trustAccountId, purchaseId);

  const handleSave = form.handleSubmit(async values => {
    // The signed supplementary FATCA/CRS declaration is mandatory — the API rejects the add
    // without it. The flow gates save until the declaration step is complete.
    if (!supplementaryValues) return;

    const {buyers} = values;

    const payload: AddBuyersToPurchasePayload = {
      buyers: mapBuyerEntriesToPayload(buyers),
      fatcaCrsSupplementary: supplementaryValues
    };

    try {
      const result = await addBuyersMutation.mutateAsync(payload);
      if (result.data.length > 0) onSuccess(result.data);
    } catch {
      // Surfaced via saveError
    }
  });

  const handleCancel = () => {
    form.reset(emptyFormValues);
    addBuyersMutation.reset();
  };

  const {ai, handleCancel: handleCancelWithAI} = useBuyerFormAi({
    form,
    buyerFields,
    trustAccountId,
    isSaving: addBuyersMutation.isPending,
    isSaveError: addBuyersMutation.isError,
    onSave: handleSave,
    onCancel: handleCancel
  });

  return {
    form,
    buyerFields,
    appendBuyer,
    removeBuyer,
    replaceBuyers,
    handleSave,
    handleCancel: handleCancelWithAI,
    saveError: addBuyersMutation.error,
    isSaving: addBuyersMutation.isPending,
    ai
  };
};
